import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PriorAuthActionBody,
  PriorAuthActionResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { logger } from '@/shared/lib';

import { submitPriorAuthAction, triggerPriorAuthSync } from '../api/priorAuthApi';
import { priorAuthKeys } from './keys';

export interface PriorAuthActionVariables {
  caseId: number;
  request: PriorAuthActionBody;
  /**
   * Our internal ``DosespotPriorAuthRecord.id`` (UUID7). When present the
   * mutation invalidates the cached details query for that record so the
   * wizard picks up any new ``pending_question`` or status change on the
   * next render.
   */
  recordId?: string;
  /**
   * The patient's user id. When present the mutation also invalidates the
   * patient's prior-auth list query, so the tab picks up new rows (for
   * example, a freshly cancelled case moving to a terminal status).
   */
  patientUserId?: string;
}

export function usePriorAuthActionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['priorAuth', 'action'],
    mutationFn: async ({
      caseId,
      request,
    }: PriorAuthActionVariables): Promise<PriorAuthActionResponseT> => {
      return await submitPriorAuthAction(caseId, request);
    },
    onSuccess: async (_response, variables) => {
      // Every PA action (answer / submit / upload / cancel / appeal /
      // deny) writes directly to DoseSpot, but the patient-list row is
      // served from our SQL mirror populated by the scheduled poller.
      // Kick off a sync so the CareState row reflects the DoseSpot
      // truth before we invalidate — otherwise the list briefly
      // refetches the *stale* mirror and flickers back to the old
      // status. The details query hits DoseSpot live so it doesn't
      // need the sync, but we still wait to keep ordering predictable.
      try {
        await triggerPriorAuthSync();
      } catch (err: unknown) {
        // A sync failure shouldn't block cache invalidation — the
        // details query is live, so the wizard still sees fresh DoseSpot
        // state even if our mirror stays a tick behind.
        logger.warn('priorAuth.action.sync.failed', {
          caseId: variables.caseId,
          action: variables.request.action,
          err,
        });
      }

      const invalidations: Promise<unknown>[] = [];
      if (variables.recordId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: priorAuthKeys.details(variables.recordId),
          })
        );
      }
      if (variables.patientUserId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: priorAuthKeys.patientList(variables.patientUserId),
          })
        );
      }
      await Promise.all(invalidations);
    },
  });
}
